import { ReactNode, RefObject, useEffect, useRef, useState } from "react";

import { ChatChip } from "./ChatPrimitives";
import { ChatShellEntry } from "./ChatShell";

/** Which answer the script currently expects — "hidden" means it expects a chip. */
export type ChatInputMode = "hidden" | "thin" | "adjust" | "customers";

export interface ChatScript {
	messages: ChatShellEntry[];
	isTyping: boolean;
	setIsTyping: (isTyping: boolean) => void;
	inputMode: ChatInputMode;
	setInputMode: (mode: ChatInputMode) => void;
	inputValue: string;
	setInputValue: (value: string) => void;
	scrollRef: RefObject<HTMLDivElement>;
	addAi: (node: ReactNode, chips?: ChatChip[]) => void;
	addUser: (text: string) => void;
	/**
	 * Drops the chips of every message — an answered question offers no choices.
	 * Chips marked `sticky` stay: they are standing affordances, not answers.
	 */
	clearChips: () => void;
}

/**
 * Transcript state of a scripted onboarding chat: the messages, the typing
 * indicator, which input the script expects, and the append helpers the script
 * drives it with. Kept apart from the script itself so a chat page holds only
 * its own beats.
 */
export const useChatScript = (): ChatScript => {
	const [messages, setMessages] = useState<ChatShellEntry[]>([]);
	const [isTyping, setIsTyping] = useState(false);
	const [inputMode, setInputMode] = useState<ChatInputMode>("hidden");
	const [inputValue, setInputValue] = useState("");

	const idRef = useRef(0);
	const scrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Feature-checked: jsdom (tests) has no Element.scrollTo.
		if (scrollRef.current && typeof scrollRef.current.scrollTo === "function") {
			scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
		}
	}, [messages, isTyping]);

	const addAi = (node: ReactNode, chips?: ChatChip[]) => {
		idRef.current += 1;
		setMessages((prev) => [...prev, { id: idRef.current, kind: "ai", node, chips }]);
	};

	const addUser = (text: string) => {
		idRef.current += 1;
		setMessages((prev) => [...prev, { id: idRef.current, kind: "user", node: text }]);
	};

	const clearChips = () => {
		setMessages((prev) =>
			prev.map((entry) => {
				const kept = entry.chips?.filter((chip) => chip.sticky);

				return { ...entry, chips: kept && kept.length > 0 ? kept : undefined };
			})
		);
	};

	return {
		messages,
		isTyping,
		setIsTyping,
		inputMode,
		setInputMode,
		inputValue,
		setInputValue,
		scrollRef,
		addAi,
		addUser,
		clearChips,
	};
};
